import { useMutation, useQuery, useQueryClient, type QueryKey } from '@tanstack/react-query';
import {
  tasksControllerAddAttachmentV1,
  tasksControllerBoardV1,
  tasksControllerCreateTaskV1,
  tasksControllerDeleteTaskV1,
  tasksControllerGetTaskV1,
  tasksControllerListTasksV1,
  tasksControllerMoveTaskV1,
  tasksControllerRemoveAttachmentV1,
  tasksControllerUpdateTaskV1,
} from '@/api/generated/tasks/tasks';
import {
  PresignUploadDtoScope,
  type CreateTaskDto,
  type MoveTaskDto,
  type TaskBoardDto,
  type TaskDto,
  type TaskFiltersDto,
  type TaskStatusRefDto,
  type TasksControllerListTasksV1Params,
  type UpdateTaskDto,
} from '@/api/generated/model';
import { useFileUploadMutation } from '@/modules/players';
import { auditLogKeys } from '@/shared/lib/auditLogKeys';

/**
 * Query keys for the tasks module.
 *
 * Boards and lists are separate branches because a drag only needs to touch
 * boards optimistically; both are invalidated after any write. Settings-owned
 * collections (statuses, teams, labels) have their own roots — renaming a
 * status must refresh every board, but editing a task must not refetch the
 * workflow.
 */
export const taskKeys = {
  all: ['tasks'] as const,
  boards: () => [...taskKeys.all, 'board'] as const,
  board: (filters: TaskFiltersDto) => [...taskKeys.boards(), filters] as const,
  lists: () => [...taskKeys.all, 'list'] as const,
  list: (params: TasksControllerListTasksV1Params) => [...taskKeys.lists(), params] as const,
  details: () => [...taskKeys.all, 'detail'] as const,
  detail: (id: string) => [...taskKeys.details(), id] as const,
  comments: (id: string) => [...taskKeys.all, 'comments', id] as const,
  counts: () => [...taskKeys.all, 'count'] as const,
  statuses: ['task-statuses'] as const,
  teams: ['teams'] as const,
  labels: ['task-labels'] as const,
  views: ['task-views'] as const,
  members: ['members', 'assignable'] as const,
};

export function useTaskBoardQuery(filters: TaskFiltersDto, enabled = true) {
  return useQuery({
    queryKey: taskKeys.board(filters),
    queryFn: ({ signal }) => tasksControllerBoardV1(filters, { signal }),
    placeholderData: (previous) => previous,
    enabled,
  });
}

export function useTaskListQuery(params: TasksControllerListTasksV1Params, enabled = true) {
  return useQuery({
    queryKey: taskKeys.list(params),
    queryFn: ({ signal }) => tasksControllerListTasksV1(params, { signal }),
    placeholderData: (previous) => previous,
    enabled,
  });
}

/** How many open tasks are on my plate — the sidebar badge. */
export function useMyOpenTaskCountQuery() {
  return useQuery({
    queryKey: [...taskKeys.counts(), 'mine-open'] as const,
    queryFn: ({ signal }) =>
      tasksControllerListTasksV1(
        { assigneeId: ['me'], category: ['unstarted', 'started'], limit: 1 },
        { signal },
      ),
    select: (page) => page.meta.total,
    staleTime: 60_000,
  });
}

export function useTaskQuery(id: string | undefined) {
  return useQuery({
    queryKey: taskKeys.detail(id ?? ''),
    queryFn: ({ signal }) => tasksControllerGetTaskV1(id as string, { signal }),
    enabled: Boolean(id),
  });
}

/** Everything that might show a task — used after any write. */
function useInvalidateTasks() {
  const queryClient = useQueryClient();
  return (id?: string) =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: taskKeys.boards() }),
      queryClient.invalidateQueries({ queryKey: taskKeys.lists() }),
      queryClient.invalidateQueries({ queryKey: taskKeys.counts() }),
      // Checklist screens embed tasks too.
      queryClient.invalidateQueries({ queryKey: ['onboardings'] }),
      queryClient.invalidateQueries({ queryKey: ['offboardings'] }),
      id ? queryClient.invalidateQueries({ queryKey: auditLogKeys.forSubject('task', id) }) : null,
    ]);
}

export function useCreateTaskMutation() {
  const invalidate = useInvalidateTasks();
  return useMutation({
    mutationFn: (dto: CreateTaskDto) => tasksControllerCreateTaskV1(dto),
    onSuccess: () => invalidate(),
  });
}

export function useUpdateTaskMutation(id: string) {
  const queryClient = useQueryClient();
  const invalidate = useInvalidateTasks();
  return useMutation({
    mutationFn: (dto: UpdateTaskDto) => tasksControllerUpdateTaskV1(id, dto),
    onSuccess: async (task) => {
      queryClient.setQueryData(taskKeys.detail(id), task);
      await invalidate(id);
    },
  });
}

export function useDeleteTaskMutation() {
  const queryClient = useQueryClient();
  const invalidate = useInvalidateTasks();
  return useMutation({
    mutationFn: (id: string) => tasksControllerDeleteTaskV1(id),
    onSuccess: async (_, id) => {
      queryClient.removeQueries({ queryKey: taskKeys.detail(id) });
      await invalidate();
    },
  });
}

export interface MoveVariables extends MoveTaskDto {
  id: string;
  /** The destination status as the board knows it, for the optimistic card. */
  status: TaskStatusRefDto;
}

/** Mirrors the server's midpoint rule so the optimistic card lands where it will stay. */
function positionBetween(before: number | null, after: number | null): number {
  if (before === null && after === null) return 1000;
  if (before === null) return (after as number) - 1000;
  if (after === null) return before + 1000;
  return (before + after) / 2;
}

/**
 * A drag-and-drop. Applied to every cached board immediately and rolled back
 * if the server refuses (a locked checklist step, a stale board), so dragging
 * feels instant but never lies for longer than a round trip.
 */
export function useMoveTaskMutation() {
  const queryClient = useQueryClient();
  const invalidate = useInvalidateTasks();

  return useMutation({
    mutationFn: ({ id, statusId, beforeId, afterId }: MoveVariables) =>
      tasksControllerMoveTaskV1(id, { statusId, beforeId, afterId }),
    onMutate: async (vars) => {
      await queryClient.cancelQueries({ queryKey: taskKeys.boards() });
      const snapshots = queryClient.getQueriesData<TaskBoardDto>({
        queryKey: taskKeys.boards(),
      });
      for (const [key, board] of snapshots) {
        if (!board) continue;
        queryClient.setQueryData<TaskBoardDto>(key, moveInBoard(board, vars));
      }
      return { snapshots };
    },
    onError: (_error, _vars, context) => {
      for (const [key, board] of context?.snapshots ?? []) {
        queryClient.setQueryData(key as QueryKey, board);
      }
    },
    onSettled: (_data, _error, vars) => invalidate(vars.id),
  });
}

function moveInBoard(board: TaskBoardDto, vars: MoveVariables): TaskBoardDto {
  const moving = board.tasks.find((task) => task.id === vars.id);
  if (!moving) return board;
  const find = (id?: string) =>
    id ? (board.tasks.find((task) => task.id === id)?.position ?? null) : null;
  const moved: TaskDto = {
    ...moving,
    status: vars.status,
    position: positionBetween(find(vars.beforeId), find(vars.afterId)),
  };
  const tasks = board.tasks
    .map((task) => (task.id === vars.id ? moved : task))
    .sort((a, b) => a.position - b.position);
  return { ...board, tasks };
}

/**
 * Uploads a file and attaches it to a task: presign → PUT to storage →
 * register. Reuses the players module's upload step so the presign/PUT dance
 * exists once.
 */
export function useAddAttachmentMutation(taskId: string) {
  const queryClient = useQueryClient();
  const upload = useFileUploadMutation();
  return useMutation({
    mutationFn: async ({ file, commentId }: { file: File; commentId?: string }) => {
      const storageKey = await upload.mutateAsync({
        file,
        scope: PresignUploadDtoScope['task-attachment'],
      });
      return tasksControllerAddAttachmentV1(taskId, {
        storageKey,
        fileName: file.name,
        mimeType: file.type,
        sizeBytes: file.size,
        commentId,
      });
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: taskKeys.detail(taskId) }),
        queryClient.invalidateQueries({ queryKey: taskKeys.boards() }),
      ]);
    },
  });
}

export function useRemoveAttachmentMutation(taskId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (attachmentId: string) => tasksControllerRemoveAttachmentV1(taskId, attachmentId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: taskKeys.detail(taskId) }),
  });
}
